import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { bookSlot, getServiceCenters, getServiceTypes, getTimeSlots } from '@/controller/appointment';
import { getCustomerProduct } from '@/controller/warranty';
import Input from '@/shared/components/UI/Input';
import { toast } from '@/shared/components/UI/Toast';
import useLoading from '@/shared/hooks/useLoading';
import { useTranslations } from '@/translations/useTranslations.js';

import SuccessBookAppointmentModal from './components/SuccessBookAppointmentModal';

const BookAppointment = () => {
    const { t } = useTranslations();
    const nav = useNavigate();
    const [egModal, setEgModal] = useState(false);
    const { loading, tip, toggle } = useLoading();
    const [selectedCardIndex, setSelectedCardIndex] = useState(null);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [serviceTypeID, setServiceTypeID] = useState();
    const [serviceCenters, setServiceCenters] = useState([]);
    const [serviceCenterID, setServiceCenterID] = useState();
    const [customerProducts, setCustomerProducts] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [timeSlots, setTimeSlots] = useState([]);
    const [timeSlotID, setTimeSlotID] = useState();
    const [isManualProduct, setIsManualProduct] = useState(false);

    // Form data state
    const [formData, setFormData] = useState({
        service: '',
        service_outlet: '',
        product_inventory: '',
        product_model: '',
        serial_number: '',
        date: '',
        remarks: '',
    });

    // Form errors state
    const [errors, setErrors] = useState({});

    // Add helper function to fetch time slots when all dependencies are available
    const fetchTimeSlots = async (date, serviceCenterId, serviceTypeId) => {
        if (!date || !serviceCenterId || !serviceTypeId) {
            setTimeSlots([]);
            return;
        }

        try {
            let formattedDate = dayjs(date).format('YYYY-MM-DD');
            const response = await getTimeSlots(formattedDate, serviceCenterId, serviceTypeId);

            if (response.status === 200) {
                const ts = Array.isArray(response.data?.data) ? response.data?.data : [];
                let timeSlotList = [];
                ts.forEach((e, i) => {
                    timeSlotList.push({
                        id: e.id,
                        services_appointment_slots_id: e.services_appointment_slots_id,
                        start_time: e.start_time,
                        end_time: e.end_time,
                        total_slot: e.total_slot,
                        total_booked: e.total_booked,
                        slot_time: e.start_time + ' - ' + e.end_time,
                    });
                });
                setTimeSlots(timeSlotList);
            }
        } catch (error) {
            console.error('Error fetching time slots:', error);
            setTimeSlots([]);
        }
    };

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: null,
            }));
        }
    };

    const onChangeDate = async (e) => {
        const date = e.target.value;
        if (!date) {
            setSelectedDate(null);
            setTimeSlots([]);
            handleFieldChange('date', '');
            return;
        }

        setSelectedDate(date);
        setSelectedCardIndex(null);
        setTimeSlotID(null);
        handleFieldChange('date', date);

        // Fetch time slots if service type and service center are already selected
        await fetchTimeSlots(date, serviceCenterID, serviceTypeID);
    };

    const onChangeServiceType = async (value) => {
        console.log(value);
        handleFieldChange('service', value);
        setFormData((prev) => ({
            ...prev,
            service_outlet: '',
        }));
        setSelectedDate(null);
        setTimeSlots([]);
        setSelectedCardIndex(null);
        setTimeSlotID(null);
        setServiceTypeID(value);
        setServiceCenterID(null);

        if (value) {
            await getServiceCenterList(value);
        } else {
            setServiceCenters([]);
        }
    };

    const onChangeServiceCenter = async (value) => {
        console.log(value);
        handleFieldChange('service_outlet', value);
        setSelectedCardIndex(null);
        setTimeSlotID(null);
        setServiceCenterID(value);

        // Fetch time slots if date and service type are already selected
        if (selectedDate && serviceTypeID && value) {
            await fetchTimeSlots(selectedDate, value, serviceTypeID);
        } else {
            setTimeSlots([]);
        }
    };

    const handleSelectedTimeSlot = async (index, timeSlot) => {
        setSelectedCardIndex(index);

        let serviceAppointmentSlotID = timeSlot.id;
        let totalSlot = timeSlot.total_slot;
        let totalBooked = timeSlot.total_booked;

        if (totalBooked >= totalSlot) {
            toast.error(t('message.time_slot_fully_booked'));
        } else {
            setTimeSlotID(serviceAppointmentSlotID);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.service) {
            newErrors.service = t('form.please_select');
        }

        if (!formData.service_outlet) {
            newErrors.service_outlet = t('form.please_select');
        }

        if (!isManualProduct && !formData.product_inventory) {
            newErrors.product_inventory = t('form.please_select');
        }

        if (isManualProduct && !formData.product_model && !formData.serial_number) {
            newErrors.product_model = t('form.product_manual_validation');
        }

        if (!formData.date) {
            newErrors.date = t('form.date_is_required');
        }

        if (selectedCardIndex === null) {
            newErrors.time_slot = t('form.please_select_time_slot');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onFinish = (e) => {
        e.preventDefault();
        if (validateForm()) {
            bookAppointment(formData);
        }
    };

    const disabledDate = (current) => {
        return current && current <= dayjs().startOf('day');
    };

    useEffect(() => {
        const getServiceTypeList = async () => {
            const response = await getServiceTypes();

            if (response.status === 200) {
                const stl = Array.isArray(response.data?.data) ? response.data?.data : [];
                let serviceTypeList = [{ value: '', title: '-', code: '' }];
                stl.forEach((e, i) => {
                    serviceTypeList.push({
                        value: e.id,
                        title: e.name,
                        code: e.code,
                    });
                });
                setServiceTypes(serviceTypeList);
            }
        };

        getServiceTypeList();
    }, []);

    useEffect(() => {
        const getCustomerProductList = async () => {
            const response = await getCustomerProduct();
            if (response.status === 200) {
                const cp = Array.isArray(response.data?.data) ? response.data?.data : [];
                let customerProductList = [{ value: '', title: '-' }];
                cp.forEach((e, i) => {
                    customerProductList.push({
                        value: e.inventory.id,
                        title: e.inventory.product.name,
                        serial_number: e.inventory.serial_number,
                    });
                });
                setCustomerProducts(customerProductList);
            }
        };

        getCustomerProductList();
    }, []);

    const getServiceCenterList = async (serviceType) => {
        const response = await getServiceCenters();

        if (response.status === 200) {
            const sc = Array.isArray(response.data?.data) ? response.data?.data : [];
            let serviceCenterList = [{ value: '', title: '-', code: '' }];
            sc.forEach((e, i) => {
                console.log(e.types.includes(serviceType));
                if (e.types.includes(serviceType)) {
                    serviceCenterList.push({
                        value: e.id,
                        title: e.name,
                        code: e.code,
                    });
                }
            });
            setServiceCenters(serviceCenterList);
        }
    };

    const bookAppointment = async (v) => {
        if (selectedCardIndex !== null && timeSlotID) {
            toggle(t('label.booking_appointment_status'));

            let formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');

            const body = {
                inventory_id: isManualProduct ? null : v.product_inventory,
                product:
                    isManualProduct && (v.product_model || v.serial_number)
                        ? {
                              name: v.product_model || '',
                              serial_number: v.serial_number || '',
                          }
                        : undefined,
                service_type_id: v.service,
                service_center_id: v.service_outlet,
                date: formattedDate,
                time_slot_id: timeSlotID,
                remarks: v.remarks || '',
            };

            try {
                const response = await bookSlot(body);

                if (response.status === 200) {
                    toggle();
                    toast.success(t('message.appointment_booked_success'));
                    // Small delay to let user see the success message
                    setTimeout(() => {
                        nav('/member/view-booking-appointment');
                    }, 1500);
                } else if (response.status !== 401) {
                    // Don't show toast for 401 errors - handled by global interceptor
                    toggle();
                    toast.error(response.data?.error?.message || t('message.appointment_book_failed'));
                }
            } catch (error) {
                toggle();
                toast.error(t('message.appointment_book_error'));
            }
        } else {
            toast.error(t('message.select_time_slot_required'));
        }
    };

    // Get selected service center for Appointment Summary
    const getSelectedServiceCenter = () => {
        return serviceCenters.find((sc) => sc.value === formData.service_outlet);
    };

    // Get selected product for Appointment Summary
    const getSelectedProduct = () => {
        if (isManualProduct) {
            return {
                title: formData.product_model || t('label.manual_entry'),
                serial_number: formData.serial_number,
            };
        }
        return customerProducts.find((cp) => cp.value === formData.product_inventory);
    };

    // Get selected service for Appointment Summary
    const getSelectedService = () => {
        return serviceTypes.find((st) => st.value === formData.service);
    };

    // Get selected time slot for Appointment Summary
    const getSelectedTimeSlot = () => {
        if (selectedCardIndex !== null && timeSlots[selectedCardIndex]) {
            return timeSlots[selectedCardIndex];
        }
        return null;
    };

    return (
        <>
            <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5 font-sans bg-white">
                <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                    <div className="flex flex-col justify-between gap-3 p-4">
                        <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                            {t('label.make_appointment')}
                        </p>
                        <p className="text-[#5f678c] text-sm font-normal leading-normal m-0">
                            {t('label.book_appointment_desc')}
                        </p>
                    </div>

                    <div className={`${loading ? 'relative' : ''}`}>
                        {loading && (
                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-xl">
                                <div className="flex flex-col items-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
                                    <p className="text-gray-600 text-sm">{tip || t('label.loading')}</p>
                                </div>
                            </div>
                        )}
                        <form onSubmit={onFinish} className="space-y-4">
                            {/* Service Selection */}
                            <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                                <label className="flex flex-col flex-1">
                                    <p className="text-gray-900 text-base font-medium pb-2">
                                        {t('form.service')}
                                        <span className="text-red-500 ml-1">*</span>
                                    </p>
                                    <div className="relative w-full">
                                        <select
                                            className={`w-full h-14 rounded-xl border ${errors.service ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base appearance-none cursor-pointer outline-none focus:border-blue-500 focus-ring select-arrow transition-colors`}
                                            value={formData.service}
                                            onChange={(e) => onChangeServiceType(e.target.value)}
                                        >
                                            <option value="">{t('form.please_select')}</option>
                                            {serviceTypes.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.service && <p className="text-red-500 text-sm mt-1">{errors.service}</p>}
                                </label>
                            </div>

                            {/* Service Center Selection */}
                            <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                                <label className="flex flex-col flex-1">
                                    <p className="text-gray-900 text-base font-medium pb-2">
                                        {t('form.service_outlet')}
                                        <span className="text-red-500 ml-1">*</span>
                                    </p>
                                    <div className="relative w-full">
                                        <select
                                            className={`w-full h-14 rounded-xl border ${errors.service_outlet ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base appearance-none cursor-pointer outline-none focus:border-blue-500 focus-ring select-arrow transition-colors`}
                                            value={formData.service_outlet}
                                            onChange={(e) => onChangeServiceCenter(e.target.value)}
                                        >
                                            <option value="">{t('form.please_select')}</option>
                                            {serviceCenters.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.service_outlet && (
                                        <p className="text-red-500 text-sm mt-1">{errors.service_outlet}</p>
                                    )}
                                </label>
                            </div>

                            {/* Product Selection */}
                            <div className="flex flex-col gap-4 px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="manualProduct"
                                        className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                        checked={isManualProduct}
                                        onChange={(e) => setIsManualProduct(e.target.checked)}
                                        aria-label={t('form.product_not_listed')}
                                    />
                                    <label
                                        htmlFor="manualProduct"
                                        className="text-gray-700 text-sm font-medium cursor-pointer"
                                    >
                                        {t('form.product_not_listed')}
                                    </label>
                                </div>

                                {!isManualProduct ? (
                                    <label className="flex flex-col flex-1">
                                        <p className="text-gray-900 text-base font-medium pb-2">
                                            {t('form.product')}
                                            <span className="text-red-500 ml-1">*</span>
                                        </p>
                                        <div className="relative w-full">
                                            <select
                                                className={`w-full h-14 rounded-xl border ${errors.product_inventory ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base appearance-none cursor-pointer outline-none focus:border-blue-500 focus-ring select-arrow transition-colors`}
                                                value={formData.product_inventory}
                                                onChange={(e) => handleFieldChange('product_inventory', e.target.value)}
                                            >
                                                <option value="">{t('form.please_select')}</option>
                                                {customerProducts.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.title} - {option.serial_number}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {errors.product_inventory && (
                                            <p className="text-red-500 text-sm mt-1">{errors.product_inventory}</p>
                                        )}
                                    </label>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <label className="flex flex-col flex-1">
                                            <p className="text-gray-900 text-base font-medium pb-2">
                                                {t('form.product_model_name')}
                                            </p>
                                            <Input
                                                placeholder={t('form.product_model_placeholder')}
                                                value={formData.product_model}
                                                onChange={(e) => handleFieldChange('product_model', e.target.value)}
                                                className={`h-14 rounded-xl border ${errors.product_model ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                            />
                                        </label>
                                        <label className="flex flex-col flex-1">
                                            <p className="text-gray-900 text-base font-medium pb-2">
                                                {t('form.serial_number')}
                                            </p>
                                            <Input
                                                placeholder={t('form.enter_serial_number')}
                                                value={formData.serial_number}
                                                onChange={(e) => handleFieldChange('serial_number', e.target.value)}
                                                className={`h-14 rounded-xl border ${errors.serial_number ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>

                            {/* Date Selection */}
                            <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                                <label className="flex flex-col flex-1">
                                    <p className="text-gray-900 text-base font-medium pb-2">
                                        {t('form.date')}
                                        <span className="text-red-500 ml-1">*</span>
                                    </p>
                                    <div className="relative w-full date-input">
                                        <input
                                            type="date"
                                            className={`w-full h-14 rounded-xl border ${errors.date ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base outline-none focus:border-blue-500 focus-ring cursor-pointer transition-colors`}
                                            value={formData.date}
                                            onChange={onChangeDate}
                                            min={dayjs().format('YYYY-MM-DD')}
                                        />
                                    </div>
                                    {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
                                </label>
                            </div>

                            {/* Time Slots */}
                            <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                                <div className="flex flex-col flex-1">
                                    <p className="text-gray-900 text-base font-medium pb-4">
                                        {t('form.select_time')}
                                        <span className="text-red-500 ml-1">*</span>
                                    </p>

                                    {/* Show message when dependencies are missing */}
                                    {(!formData.service || !formData.service_outlet || !formData.date) && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0">
                                                    <svg
                                                        className="h-5 w-5 text-blue-400"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm text-blue-700">
                                                        {t('label.select_dependencies_message')}
                                                    </p>
                                                    <div className="mt-2 text-xs text-blue-600">
                                                        {t('label.missing_fields')}:
                                                        {!formData.service && (
                                                            <span className="ml-1 bg-blue-100 px-2 py-1 rounded">
                                                                {t('form.service')}
                                                            </span>
                                                        )}
                                                        {!formData.service_outlet && (
                                                            <span className="ml-1 bg-blue-100 px-2 py-1 rounded">
                                                                {t('form.service_outlet')}
                                                            </span>
                                                        )}
                                                        {!formData.date && (
                                                            <span className="ml-1 bg-blue-100 px-2 py-1 rounded">
                                                                {t('form.date')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Show time slots when available */}
                                    {timeSlots.length > 0 && (
                                        <div className="flex flex-wrap gap-3">
                                            {timeSlots.map((timeSlot, index) => {
                                                const isDisabled = timeSlot.total_booked >= timeSlot.total_slot;
                                                const isSelected = selectedCardIndex === index;

                                                return (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        disabled={isDisabled}
                                                        onClick={() => handleSelectedTimeSlot(index, timeSlot)}
                                                        className={`
                                                            px-4 py-3 min-w-[120px] h-12 rounded-xl text-sm font-medium transition-all duration-200 border relative
                                                            ${
                                                                isDisabled
                                                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                                    : isSelected
                                                                      ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                                                                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50 cursor-pointer'
                                                            }
                                                            `}
                                                    >
                                                        {timeSlot.slot_time}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Show message when no time slots available for selected criteria */}
                                    {formData.service &&
                                        formData.service_outlet &&
                                        formData.date &&
                                        timeSlots.length === 0 && (
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0">
                                                        <svg
                                                            className="h-5 w-5 text-yellow-400"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className="text-sm text-yellow-700">
                                                            {t('label.no_time_slots_available')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                    {errors.time_slot && (
                                        <p className="text-red-500 text-sm mt-2">{errors.time_slot}</p>
                                    )}
                                </div>
                            </div>

                            {/* Remarks Field */}
                            <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                                <label className="flex flex-col flex-1">
                                    <p className="text-gray-900 text-base font-medium pb-2">{t('form.remarks')}</p>
                                    <textarea
                                        value={formData.remarks}
                                        onChange={(e) => handleFieldChange('remarks', e.target.value)}
                                        placeholder={t('form.enter_remarks')}
                                        maxLength={250}
                                        rows={6}
                                        className={`w-full rounded-xl border ${errors.remarks ? 'border-red-500' : 'border-gray-300'} bg-white px-4 py-3 text-base outline-none focus:border-blue-500 resize-none transition-colors`}
                                    />
                                    {errors.remarks && <p className="text-red-500 text-sm mt-1">{errors.remarks}</p>}
                                    <p className="text-gray-500 text-xs mt-1">
                                        {formData.remarks.length}/250 characters
                                    </p>
                                </label>
                            </div>

                            {/* Appointment Summary */}
                            {(formData.service ||
                                formData.service_outlet ||
                                formData.product_inventory ||
                                formData.date ||
                                selectedCardIndex !== null) && (
                                <div className="px-4 py-3">
                                    <p className="text-gray-900 text-lg font-medium pb-4">
                                        {t('label.appointment_summary')}
                                    </p>
                                    <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                                        {getSelectedService()?.title && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">{t('form.service')}:</span>
                                                <span className="text-gray-900 font-medium">
                                                    {getSelectedService().title}
                                                </span>
                                            </div>
                                        )}
                                        {getSelectedServiceCenter()?.title && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">{t('form.service_outlet')}:</span>
                                                <span className="text-gray-900 font-medium">
                                                    {getSelectedServiceCenter().title}
                                                </span>
                                            </div>
                                        )}
                                        {getSelectedProduct()?.title && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">{t('form.product')}:</span>
                                                <span className="text-gray-900 font-medium">
                                                    {getSelectedProduct().title}
                                                </span>
                                            </div>
                                        )}
                                        {getSelectedProduct()?.serial_number && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">{t('form.serial_number')}:</span>
                                                <span className="text-gray-900 font-medium">
                                                    {getSelectedProduct().serial_number}
                                                </span>
                                            </div>
                                        )}
                                        {formData.date && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">{t('form.date')}:</span>
                                                <span className="text-gray-900 font-medium">
                                                    {dayjs(formData.date).format('MMMM DD, YYYY')}
                                                </span>
                                            </div>
                                        )}
                                        {getSelectedTimeSlot() && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">{t('form.time')}:</span>
                                                <span className="text-gray-900 font-medium">
                                                    {getSelectedTimeSlot().slot_time}
                                                </span>
                                            </div>
                                        )}
                                        {formData.remarks && (
                                            <div className="flex flex-col">
                                                <span className="text-gray-600 mb-1">{t('form.remarks')}:</span>
                                                <span className="text-gray-900 font-medium whitespace-pre-wrap break-words">
                                                    {formData.remarks}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Submit Button */}
                            <div className="p-4 flex justify-center">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-4 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
                                >
                                    <span className="overflow-hidden">
                                        {loading ? t('label.loading') : t('label.book_appointment')}
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <SuccessBookAppointmentModal visible={egModal} onClose={() => setEgModal(false)} />
        </>
    );
};

export default BookAppointment;
